import {makeStyles} from "@material-ui/core/styles";
import React, {FunctionComponent, useState} from 'react'
import {FixedSizeList} from 'react-window'

import {
  TextField,
  Card,
  FormLabel,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select as RegularSelect,
  OutlinedInput,
  MenuItem,
  Typography, Box
} from "@material-ui/core"
import {TextFieldProps} from '@material-ui/core/TextField'
import {Location} from '@reach/router'
import Mutation from 'react-apollo/Mutation'
import queryString from 'query-string'
import Select from 'react-select'

import {SIGN_UP_USER, SIGN_UP_CONTRACTOR} from '../../apollo/mutations'
import {useGetCounties} from '../../apollo/queries'
import Loading from '../ui/Loading';
import ResponsiveModal from "../ui/ResponsiveModal"

const FairmarketUserAgreement = require('../../images/Fairmarket-User-Agreement.pdf');
const PrivacyPolicy = require('../../images/Privacy-Policy.pdf');

const makeProps = (name, classes, errors): TextFieldProps => {
  const {[name]: value} = name
  return {
    required: true,
    variant: 'outlined',
    className: classes.textField,
    name,
    value,
    margin: 'dense',
    error: !!errors[name],
    helperText: errors[name] || undefined
  }
}

export enum AccountType {
  HOME_OWNER,
  CONTRACTOR
}

interface CreateAccountProps {
  skipEmail?: boolean
  onSignup?: () => void
  classes: Record<string, any>
  hasTrades: boolean
  submitting: boolean
  accountType: AccountType
  selectedTrades: {
    value: [{
      id: string
      group: string
    }]
  }
}

const useStyles = makeStyles(theme => ({
  select: {
    width: '100%',
  },
  selectWrapper: {
    width: "100%",
    minWidth: "84px",
    marginTop: "6px",
    marginLeft: "8px",
    marginRight: "8px"
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column'
  },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
    minWidth: 84,
    marginTop: 6
  },
  inputCard: {
    padding: 22,
    width: 542,
    maxWidth: '95vw',
    margin: 12
  },
  formLabel: {
    marginBottom: 12
  },
  formTitle: {
    marginLeft: 8,
    width: '95vw',
    alignSelf: 'center',
    fontWeight: 'bold',
    color: '#333'
  },
  submit: {
    margin: 12,
    paddingTop: 12,
    paddingBottom: 12
  },
  questionMark: {
    fontSize: 14,
  },
  neighbourhoodCodeText: {
    margin: '8px',
  }

}));

const selectStyles = {
  placeholder: (provided) => ({
    ...provided,
    fontSize: '1rem',
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.54)'
  })
}

const useTextFieldStyles = makeStyles({
  root: {
    zIndex: 0
  }
});


const CompanyInfo: FunctionComponent<CreateAccountProps> = ({
                                                              hasTrades,
                                                              accountType,
                                                              selectedTrades,
                                                              skipEmail,
                                                              onSignup
                                                            }: CreateAccountProps) => {

  const inputLabel = React.useRef(null);
  const [labelWidth, setLabelWidth] = React.useState(120);
  React.useEffect(() => {
    inputLabel.current && setLabelWidth(inputLabel.current.offsetWidth);
  }, []);

  const [localError, setLocalError] = useState('')
  const [userType, setUserType] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [companyPosition, setCompanyPosition] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [theStates, setTheStates] = useState<string[]>([])
  const [selectedCounties, setSelectedCounties] = useState([])
  const [agreedTOS, setAgreedTOS] = useState(false)
  const [verifyEmail, setVerifyEmail] = useState(false)
  const {
    data, loading,
  } = useGetCounties();
  const states = data?.states;

  const classes = useStyles();
  const textFieldClasses = useTextFieldStyles();

  if (loading) return <Card className={classes.inputCard}><Loading relative dark style={{paddingTop: '40%'}}/></Card>
  const countiesWithState = (states || []).map(s => s.counties.map(c => ({
    ...c,
    state: s
  }))).reduce((arr, c) => arr.concat(c), []);
  const counties = countiesWithState.map(c => Object.assign({}, c, {label: `${c.name}, ${c.state.name}`, value: c.id}))
  const errors = false

  return (
    <Location>
      {({location}) => (
        <Mutation
          mutation={
            accountType === AccountType.HOME_OWNER
              ? SIGN_UP_USER
              : SIGN_UP_CONTRACTOR
          }
          onCompleted={data => {
            if (onSignup) {
              return onSignup()
            }
            setVerifyEmail(true);
          }}
        >
          {(signUp, {loading, error, data}) => (
            <>
              <form
                className={classes.container}
                autoComplete="off"
                onSubmit={e => {
                  e.preventDefault()
                  setLocalError('')
                  if (password !== passwordConfirmation) {
                    setLocalError('Passwords do not match')
                    return;
                  }
                  const variables = {
                    email,
                    password,
                    skipEmail,
                    source: 'web'
                  }
                  const qs = queryString.parse(location.hash)

                  accountType === AccountType.HOME_OWNER
                    ? Object.assign(variables, {
                      firstName,
                      lastName,
                      purpose: userType
                    })
                    : Object.assign(variables, {
                      companyName,
                      companyPosition,
                      companyAdminName: `${firstName} ${lastName}`,
                      trades: selectedTrades.value.map(st => st.id),
                      counties: selectedCounties,
                      jobId: qs.job_id,
                    })
                  signUp({variables})
                }}
              >
                <Card className={classes.inputCard}>
                  {accountType !== AccountType.HOME_OWNER && (
                    <FormLabel component="legend" className={classes.formLabel}>
                      Company Info
                    </FormLabel>)}
                  {accountType !== AccountType.HOME_OWNER && (
                    <TextField
                      {...makeProps('companyName', classes, errors)}
                      onChange={e => setCompanyName(e.target.value)}
                      label="Company Name"
                      fullWidth
                      value={companyName}
                    />
                  )}
                  <TextField
                    {...makeProps('firstName', classes, errors)}
                    onChange={e => setFirstName(e.target.value)}
                    label="First Name"
                    fullWidth
                    value={firstName}
                  />
                  <TextField
                    {...makeProps('lastName', classes, errors)}
                    onChange={e => setLastName(e.target.value)}
                    label="Last Name"
                    fullWidth
                    value={lastName}
                  />
                  {accountType !== AccountType.HOME_OWNER && (
                    <TextField
                      {...makeProps('companyPosition', classes, errors)}
                      required={false}
                      onChange={e => setCompanyPosition(e.target.value)}
                      label="Position in Company"
                      fullWidth
                      value={companyPosition}
                    />
                  )}
                  {accountType === AccountType.HOME_OWNER ? (
                    <FormControl fullWidth className={classes.textField}
                                 variant="outlined" {...makeProps('userType', classes, errors)}>
                      <InputLabel ref={inputLabel} style={{backgroundColor: 'white'}} htmlFor="user-type">
                        User Type
                      </InputLabel>
                      <RegularSelect
                        {...makeProps('userType', {}, errors)}
                        onChange={e => setUserType(e.target.value)}
                        label="User Type"
                        value={userType}
                        input={<OutlinedInput labelWidth={labelWidth} name="userType" id="user-type"/>}
                      >
                        <MenuItem value=""><em>Select a User Type</em></MenuItem>
                        <MenuItem value="HOMEOWNER">Homeowner</MenuItem>
                        <MenuItem value="PROPERTY_MANAGER">Property Manager</MenuItem>
                        <MenuItem value="REALTOR">Realtor</MenuItem>
                        <MenuItem value="HOUSE_FLIPPER">House Flipper</MenuItem>
                      </RegularSelect>
                    </FormControl>
                  ) : (
                    <>
                      <Box className={classes.selectWrapper}>
                        <Select
                          placeholder="Select States You Perform Work In"
                          label="States You Serve"
                          isMulti
                          variant="filled"
                          styles={selectStyles}
                          options={states.map(s => ({...s, label: s.name, value: s.name}))}
                          className={classes.select}
                          onChange={(selectedStates = []) => setTheStates(selectedStates ? selectedStates.map(c => c.value) : [])}
                          components={{MenuList}}
                        />
                      </Box>
                      <Box component="div" className={classes.selectWrapper}>
                        <Select
                          placeholder={theStates.length > 0 ? "Counties You Perform Work In" : "Counties... (select a state first)"}
                          label="Counties You Serve"
                          isMulti
                          variant="filled"
                          styles={selectStyles}
                          options={theStates.length > 0 ? counties.filter(c => theStates.includes(c.state.name)) : counties}
                          className={classes.select}
                          onChange={(selectedCounties = []) => setSelectedCounties(selectedCounties ? selectedCounties.map(c => c.value) : [])}
                          components={{MenuList}}
                        />
                      </Box>
                    </>
                  )}
                  <TextField
                    {...makeProps('email', classes, errors)}
                    onChange={e => setEmail(e.target.value)}
                    label="Email"
                    fullWidth
                    value={email}
                    classes={textFieldClasses}
                    autoComplete="username email"
                  />
                  <TextField
                    {...makeProps('password', classes, errors)}
                    onChange={e => setPassword(e.target.value)}
                    label="Password"
                    fullWidth
                    type="password"
                    value={password}
                    classes={textFieldClasses}
                    autoComplete="new-password"
                  />
                  <TextField
                    {...makeProps('confirmPassword', classes, errors)}
                    onChange={e => setPasswordConfirmation(e.target.value)}
                    label="Confirm Password"
                    fullWidth
                    type="password"
                    classes={textFieldClasses}
                    autoComplete="new-password"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreedTOS}
                        onChange={() => setAgreedTOS(!agreedTOS)}
                      />
                    }
                    label={<>
                      I agree with the <a download href={FairmarketUserAgreement}>User Agreement</a>, <a download
                                                                                                         href={PrivacyPolicy}>Privacy
                      Policy.</a> and <a target={"_blank"} href={"https://view-p1.geomni.net/public/EULA.htm"}>EULA</a>
                    </>}
                  />
                  {(error || localError) &&
									<Typography style={{margin: 12, color: 'red'}}>{localError || error.message}</Typography>}
                </Card>
                <Button
                  disabled={loading || !hasTrades || !agreedTOS}
                  type="submit"
                  className={classes.submit}
                >
                  {loading ? 'Loading...' : 'Submit'}
                </Button>
              </form>
              <ResponsiveModal open={verifyEmail} onClose={() => null} closeButton={false}>
                <Typography variant={"h6"}>You're almost done!</Typography>
                <Typography>We've sent you a verification email, please check your inbox and click the link to verify
                  you account.</Typography>
                <Typography>Check spam just in case!</Typography>
              </ResponsiveModal>
            </>
          )}
        </Mutation>
      )}
    </Location>
  )
}

export default CompanyInfo

const MenuList = ({children, maxHeight}: { children?: any, maxHeight?: number }) => (
  <FixedSizeList
    height={maxHeight}
    itemCount={children.length}
    itemSize={42}
  >
    {({index, style}) => <div style={style}>{children[index]}</div>}
  </FixedSizeList>
);
